<script setup>
  import { onMounted, onBeforeUnmount, ref } from 'vue'
  import Player from '@vimeo/player'
  import { useLessonProgress } from '../../composables/useLessonProgress'
  import { TransformLearningProgressService } from '../../services/TransformLearningProgressService'
  import { handleErrors } from '../../utils/errorHandler'

  const props = defineProps({
    // Vimeo numeric video id from bcst_lessons.vimeoVideoId.
    vimeoVideoId: { type: [String, Number], default: null },
    lessonId: { type: String, required: true },
    courseId: { type: String, required: true },
    durationSeconds: { type: Number, default: 0 },
    // Previously-saved progress used to seed the monotonic watch tracker.
    initialPercent: { type: Number, default: 0 },
    initialCompleted: { type: Boolean, default: false },
    // Saved playhead position (seconds) to resume from where the user left off.
    initialPositionSeconds: { type: Number, default: 0 },
    // Previously watched segments — seed cumulative coverage across sessions.
    initialSegments: { type: Array, default: () => [] }
  })

  const containerRef = ref(null)
  const isLoading = ref(true)
  const hasError = ref(false)

  let player = null

  // Our branded loader stays up until real playback starts, so Vimeo's own buffering
  // spinner never shows through underneath — a single, consistent loading state.
  let loadingFallbackTimer = null
  function finishLoading() {
    if (loadingFallbackTimer) {
      clearTimeout(loadingFallbackTimer)
      loadingFallbackTimer = null
    }
    isLoading.value = false
  }

  const embedService = TransformLearningProgressService()

  const { recordTime, flush, markEnded, completed } = useLessonProgress({
    lessonId: props.lessonId,
    courseId: props.courseId,
    durationSeconds: props.durationSeconds,
    initialPercent: props.initialPercent,
    initialCompleted: props.initialCompleted,
    initialPositionSeconds: props.initialPositionSeconds,
    initialSegments: props.initialSegments
  })

  // Maximum playhead position legitimately reached across sessions. Used to block
  // forward seeking via the scrubber for non-completed lessons. Seeded from the
  // persisted resume position AND from the furthest end of any previously watched
  // segment, so returning learners can't jump past content they already covered.
  const segmentMaxEnd = (props.initialSegments || []).reduce((max, seg) => {
    const end = Number(seg?.end)
    return Number.isFinite(end) && end > max ? end : max
  }, 0)
  let maxPlayedSeconds = Math.max(0, Number(props.initialPositionSeconds) || 0, segmentMaxEnd)

  function fail(context, error) {
    hasError.value = true
    finishLoading()
    if (error) handleErrors(`TransformLessonVideoPlayer.${context}`, error, { sendToSentry: false })
  }

  onMounted(async () => {
    if (!props.vimeoVideoId || !containerRef.value) {
      fail('missingVideoId')
      return
    }

    // BCST videos are private, so the id alone is rejected. Resolve the embeddable
    // URL (with private hash) from the backend, which holds the Vimeo token.
    let embedUrl
    try {
      const embed = await embedService.getVideoEmbed(props.vimeoVideoId)
      embedUrl = embed?.embedUrl
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TLVP] getVideoEmbed failed', err)
      fail('resolveEmbed', err)
      return
    }
    if (import.meta.env.DEV)
      console.log('[TLVP] vimeoVideoId', props.vimeoVideoId, '→ embedUrl', embedUrl)
    if (!embedUrl || !containerRef.value) {
      fail('resolveEmbed')
      return
    }

    // Block forward-seeking (skip/scrub ahead) until the lesson is completed — the
    // learner can always rewind, only advancing past the furthest point is disallowed.
    // Applied client-side (not on the cached backend embedUrl) since completion is
    // per-user while the resolved embed URL is cached per video across all users.
    const finalUrl = new URL(embedUrl)
    finalUrl.searchParams.set('skipping_forward', props.initialCompleted ? 'true' : 'false')
    if (import.meta.env.DEV) {
      console.log(
        '[TLVP] initialCompleted',
        props.initialCompleted,
        '→ skipping_forward param set to',
        finalUrl.searchParams.get('skipping_forward')
      )
      console.log('[TLVP] final player url', finalUrl.toString())
    }

    try {
      // Videos are unlisted + embed-whitelisted, so Vimeo's oEmbed works on the
      // whitelisted domain and @vimeo/player initializes normally from the embed URL.
      player = new Player(containerRef.value, {
        url: finalUrl.toString(),
        responsive: true,
        // Start playing as soon as the lesson opens.
        autoplay: true
        // Use the native Vimeo player chrome/controls (title, author, owner-logo overlay
        // and Picture-in-Picture). Forward-seek stays restricted separately (see below).
      })

      player
        .ready()
        .then(async () => {
          // Do NOT hide the loader here: `ready` fires before the first frame renders,
          // so hiding now would expose Vimeo's own buffering spinner. Keep our loader
          // until actual playback (playing/timeupdate/bufferend), with a safety timeout
          // in case autoplay is blocked and no playback event ever fires.
          loadingFallbackTimer = setTimeout(finishLoading, 6000)
          // Resume from the saved playhead position (skip if effectively at the start).
          if (props.initialPositionSeconds > 1) {
            try {
              await player.setCurrentTime(props.initialPositionSeconds)
            } catch (_e) {
              /* clamp/ignore */
            }
          }
          // Ensure it starts. Browsers block autoplay-with-sound without a prior
          // gesture, so if that's rejected, retry muted (still fires timeupdate).
          return player.play().catch(() => {
            player.setMuted(true)
            return player.play().catch(() => {})
          })
        })
        .catch((err) => {
          if (import.meta.env.DEV) console.error('[TLVP] player.ready failed', err)
          fail('ready', err)
        })

      // Guard used while programmatically snapping the playhead back, so our own
      // setCurrentTime() calls don't trigger an infinite enforcement loop.
      let isEnforcingSeek = false

      async function snapToBoundary(label, targetSeconds) {
        if (completed.value || isEnforcingSeek) return
        if (targetSeconds <= maxPlayedSeconds + 1) return
        if (import.meta.env.DEV) {
          console.log(
            '[TLVP] blocking forward seek (',
            label,
            ')',
            targetSeconds,
            '→ maxPlayedSeconds',
            maxPlayedSeconds
          )
        }
        isEnforcingSeek = true
        try {
          await player.setCurrentTime(maxPlayedSeconds)
        } catch (_e) {
          /* ignore */
        } finally {
          isEnforcingSeek = false
        }
      }

      // `timeupdate` fires both during honest playback and right after a seek.
      // We only advance maxPlayedSeconds and report watched seconds when the
      // delta from the previous reported time is small (normal playback). Large
      // forward jumps are ignored because they come from scrubbing/seeking, not
      // from watching, and must not inflate the progress coverage.
      let lastTimeUpdateSeconds = maxPlayedSeconds
      player.on('timeupdate', (data) => {
        // First frames are rendering — safe to reveal the video and drop our loader.
        finishLoading()
        const seconds = data?.seconds ?? 0
        const isLargeForwardSeek = seconds > lastTimeUpdateSeconds + 2
        const smallForwardJump =
          seconds > lastTimeUpdateSeconds && !isLargeForwardSeek
        if (smallForwardJump && seconds > maxPlayedSeconds) {
          maxPlayedSeconds = seconds
        }
        if (!isLargeForwardSeek) {
          recordTime(seconds)
        }
        lastTimeUpdateSeconds = seconds
      })

      // Actual playback / buffer completion — reveal the video (single loading state).
      player.on('playing', () => finishLoading())
      player.on('bufferend', () => finishLoading())

      player.on('pause', () => flush())
      player.on('ended', () => markEnded())

      if (import.meta.env.DEV) {
        player.on('seeking', (data) => console.log('[TLVP] seeking →', data))
        player.on('seeked', (data) => console.log('[TLVP] seeked →', data))
      }

      // Snap back on any completed seek that went past the allowed boundary.
      player.on('seeked', async (data) => {
        await snapToBoundary('seeked', data?.seconds ?? 0)
      })

      // Critical fallback: when the player is paused, setCurrentTime() may not
      // visibly apply until playback resumes. Re-check the boundary on play so
      // a user can't pause → seek forward → unpause to watch ahead.
      player.on('play', async () => {
        try {
          const current = await player.getCurrentTime()
          await snapToBoundary('play', current)
        } catch (_e) {
          /* ignore */
        }
      })

      player.on('error', (err) => {
        if (import.meta.env.DEV) console.error('[TLVP] vimeo player error', err)
        fail('playerError', err)
      })
    } catch (err) {
      fail('init', err)
    }
  })

  onBeforeUnmount(() => {
    if (loadingFallbackTimer) {
      clearTimeout(loadingFallbackTimer)
      loadingFallbackTimer = null
    }
    // Vue does not await async unmount hooks, so tear the player down synchronously first
    // (stops its events/timers deterministically before the DOM node is removed), then
    // fire-and-forget the final progress write — flush() works off already-recorded state,
    // not the player, so it is safe to run after destroy().
    const activePlayer = player
    player = null
    if (activePlayer) activePlayer.destroy().catch(() => {})
    flush()
  })
</script>

<template>
  <div class="tlvp-wrapper">
    <!-- Error: invalid/missing video id or player failure -->
    <div v-if="hasError" class="tlvp-state tlvp-state--error">
      <i class="bi bi-exclamation-triangle tlvp-state-icon"></i>
      <p class="tlvp-state-text">This video is currently unavailable.</p>
    </div>

    <template v-else>
      <!-- Loading overlay until the Vimeo player is ready -->
      <div v-if="isLoading" class="tlvp-state tlvp-state--loading">
        <div class="spinner-border text-light" role="status">
          <span class="visually-hidden">Loading…</span>
        </div>
      </div>
      <!-- Vimeo mounts its iframe inside this container -->
      <div ref="containerRef" class="tlvp-player"></div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  /* Figma player size 948×532 (≈16:9). Fills the available main-column width
     and scales down responsively within the column instead of overflowing. */
  .tlvp-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    /* Subtle definition so white videos don't blend into the page — matches the
       project's shadow language (8px blur, 0.1 alpha; cf. cards / offcanvas). */
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
  }

  .tlvp-player {
    width: 100%;
    height: 100%;

    :deep(iframe) {
      display: block;
      width: 100%;
      height: 100%;
      /* Round the iframe itself: some browsers don't clip a positioned iframe to the
         wrapper's border-radius, so its square black corners poke past the rounding. */
      border-radius: 12px;
    }
  }

  .tlvp-state {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    padding: 16px;
  }

  .tlvp-state--loading {
    background-color: #000;
    z-index: 1;
  }

  .tlvp-state--error {
    background-color: var(--bs-gray-900, #212529);
    color: var(--bs-gray-300, #dee2e6);
  }

  .tlvp-state-icon {
    font-size: 2rem;
  }

  .tlvp-state-text {
    margin: 0;
    font-size: 0.95rem;
  }
</style>
